/**
 * Dedicated Meta Ads acquisition landing page — route /meta-proposals only.
 * Not linked from the app navigation. All UI below is a product visualisation:
 * no customer names, logos, testimonials or business results are claimed.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, useInView, useScroll, useSpring } from "framer-motion";
import {
  ArrowRight, BadgeCheck, Ban, BarChart3, Briefcase, Building2, Check, Clock, Code2,
  Eye, FileText, Highlighter, IndianRupee, Layers, Megaphone, MousePointer2, PenLine,
  Rocket, Send, Sparkles, Timer, TrendingUp, Users, Zap,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { track } from "@/lib/meta";
import { INR_PRICES } from "@/lib/pricing";

/* ---------------------------------- shell --------------------------------- */

const Reveal = ({ children, delay = 0, y = 18, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Section = ({ eyebrow, title, sub, children, className = "", testid }) => (
  <section data-testid={testid} className={`mx-auto max-w-6xl px-5 py-16 sm:py-24 ${className}`}>
    <Reveal>
      {eyebrow && (
        <p className="text-[10px] sm:text-[11px] font-num uppercase tracking-[0.24em] text-indigo-400">{eyebrow}</p>
      )}
      {title && <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">{title}</h2>}
      {sub && <p className="mt-3 max-w-2xl text-sm md:text-base text-zinc-400">{sub}</p>}
    </Reveal>
    <div className="mt-8 sm:mt-12">{children}</div>
  </section>
);

/* ------------------------------- product bits ------------------------------ */

const Window = ({ label, children, className = "" }) => (
  <div className={`rounded-2xl border border-zinc-800 bg-[#0c0c11] overflow-hidden shadow-2xl ${className}`}>
    <div className="flex items-center gap-1.5 px-3.5 py-2.5 border-b border-zinc-800/80 bg-zinc-900/40">
      <span className="w-2 h-2 rounded-full bg-red-400/70" />
      <span className="w-2 h-2 rounded-full bg-amber-400/70" />
      <span className="w-2 h-2 rounded-full bg-emerald-400/70" />
      <span className="ml-2 text-[10px] font-num uppercase tracking-[0.16em] text-zinc-500">{label}</span>
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

const STATUSES = [
  { label: "Draft", icon: PenLine, tone: "text-zinc-400 border-zinc-700" },
  { label: "Generated", icon: Sparkles, tone: "text-indigo-300 border-indigo-500/50" },
  { label: "Sent", icon: Send, tone: "text-sky-300 border-sky-500/50" },
  { label: "Viewed", icon: Eye, tone: "text-amber-300 border-amber-500/50" },
  { label: "Accepted", icon: BadgeCheck, tone: "text-emerald-300 border-emerald-500/50" },
];

/** Hero pipeline: one proposal moving from draft to accepted. */
const StatusPipeline = () => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % (STATUSES.length + 1)), 1400);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="hero-status-pipeline" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
      {STATUSES.map((s, i) => {
        const on = i <= step;
        const Icon = s.icon;
        return (
          <motion.span
            key={s.label}
            animate={{ opacity: on ? 1 : 0.32, scale: i === step ? 1.04 : 1 }}
            transition={{ duration: 0.35 }}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] sm:text-[11px] font-medium ${
              on ? s.tone : "text-zinc-600 border-zinc-800"
            }`}
          >
            <Icon className="w-3 h-3" />
            {s.label}
          </motion.span>
        );
      })}
    </div>
  );
};

const Counter = ({ to, prefix = "", suffix = "" }) => {
  const ref = useRef(null);
  const seen = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!seen) return;
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / 900);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [seen, to]);
  return (
    <span ref={ref} className="font-num">
      {prefix}
      {n.toLocaleString("en-IN")}
      {suffix}
    </span>
  );
};

const PROPOSAL_SECTIONS = [
  { label: "Project overview", icon: FileText },
  { label: "Scope of work", icon: Layers },
  { label: "Deliverables", icon: Check },
  { label: "Timeline", icon: Timer },
  { label: "Pricing", icon: IndianRupee },
  { label: "Payment breakdown", icon: BarChart3 },
  { label: "Invoice details", icon: Briefcase },
  { label: "Terms", icon: BadgeCheck },
];

/** Sections assembling one by one, as they do while a proposal is generated. */
const AssemblingProposal = ({ testid = "proposal-assembly" }) => {
  const ref = useRef(null);
  const seen = useInView(ref, { once: true, margin: "-80px" });
  return (
    <div ref={ref} data-testid={testid} className="grid sm:grid-cols-2 gap-2">
      {PROPOSAL_SECTIONS.map((s, i) => (
        <motion.div
          key={s.label}
          initial={{ opacity: 0, x: -12 }}
          animate={seen ? { opacity: 1, x: 0 } : {}}
          transition={{ delay: i * 0.12, duration: 0.4 }}
          className="flex items-center gap-2.5 rounded-xl border border-zinc-800 bg-zinc-900/40 px-3 py-2.5"
        >
          <s.icon className="w-3.5 h-3.5 text-indigo-300 shrink-0" />
          <span className="text-xs text-zinc-300">{s.label}</span>
          <motion.span
            initial={{ scale: 0 }}
            animate={seen ? { scale: 1 } : {}}
            transition={{ delay: i * 0.12 + 0.25, type: "spring", stiffness: 320 }}
            className="ml-auto"
          >
            <Check className="w-3.5 h-3.5 text-emerald-400" />
          </motion.span>
        </motion.div>
      ))}
    </div>
  );
};

const QUESTIONS = [
  { q: "Who is the client?", a: "Northwind Retail Pvt Ltd" },
  { q: "What is the project?", a: "Website redesign + SEO" },
  { q: "What do they need?", a: "12 pages, CMS, analytics" },
  { q: "What is the scope?", a: "Design, build, migrate, train" },
  { q: "What is the timeline?", a: "6 weeks, 3 milestones" },
  { q: "What is the price?", a: "₹4,80,000 · 40/40/20" },
];

/** Typing answers into the wizard, then handing over to generation. */
const WizardDemo = () => {
  const ref = useRef(null);
  const seen = useInView(ref, { once: true, margin: "-100px" });
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!seen) return;
    const t = setInterval(() => setI((v) => Math.min(v + 1, QUESTIONS.length)), 900);
    return () => clearInterval(t);
  }, [seen]);
  return (
    <div ref={ref} data-testid="wizard-demo" className="space-y-2">
      {QUESTIONS.map((row, idx) => (
        <motion.div
          key={row.q}
          initial={{ opacity: 0, y: 10 }}
          animate={idx <= i ? { opacity: 1, y: 0 } : { opacity: 0.2, y: 0 }}
          transition={{ duration: 0.35 }}
          className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-3.5 py-2.5"
        >
          <p className="text-[11px] text-zinc-500">{row.q}</p>
          <p className="mt-0.5 text-sm text-zinc-100">
            {idx < i ? row.a : idx === i ? <TypeLine text={row.a} /> : <span className="text-zinc-700">—</span>}
          </p>
        </motion.div>
      ))}
      <AnimatePresence>
        {i >= QUESTIONS.length && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 rounded-xl border border-indigo-500/40 bg-indigo-500/10 px-3.5 py-3"
          >
            <Sparkles className="w-4 h-4 text-indigo-300" />
            <span className="text-xs text-indigo-200">Generating your proposal…</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const TypeLine = ({ text }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    setN(0);
    const t = setInterval(() => setN((v) => (v >= text.length ? v : v + 1)), 26);
    return () => clearInterval(t);
  }, [text]);
  return (
    <span>
      {text.slice(0, n)}
      <span className="inline-block w-1.5 h-3.5 -mb-0.5 bg-indigo-400 animate-pulse" />
    </span>
  );
};

const TRANSCRIPT = [
  "Client: We need the new site live before the festive sale.",
  "Us: That's about six weeks including migration.",
  "Client: Budget is around 5 lakh, can we split payments?",
  "Us: 40% upfront, 40% at design sign-off, 20% on launch.",
];

/** Transcript → understanding → generated proposal. */
const TranscriptDemo = () => {
  const ref = useRef(null);
  const seen = useInView(ref, { once: true, margin: "-100px" });
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    if (!seen) return;
    const timers = [900, 2600, 4200].map((ms, i) => setTimeout(() => setPhase(i + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, [seen]);
  return (
    <div ref={ref} data-testid="transcript-demo" className="grid lg:grid-cols-[1fr_auto_1fr] gap-4 items-center">
      <Window label="meeting transcript">
        <div className="space-y-2">
          {TRANSCRIPT.map((line, i) => (
            <motion.p
              key={line}
              initial={{ opacity: 0, x: -10 }}
              animate={seen ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: i * 0.22 }}
              className="text-[11px] leading-relaxed text-zinc-400"
            >
              {line}
            </motion.p>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={phase >= 1 ? { opacity: 1 } : {}}
          className="mt-3 flex items-center gap-2 text-[11px] text-indigo-300"
        >
          <MousePointer2 className="w-3 h-3" /> Pasted into Propojo
        </motion.div>
      </Window>

      <motion.div
        animate={phase >= 1 ? { opacity: 1, scale: 1 } : { opacity: 0.3, scale: 0.9 }}
        className="hidden lg:grid place-items-center"
      >
        <div className="relative h-12 w-12 grid place-items-center rounded-2xl border border-indigo-500/40 bg-indigo-500/10">
          <Zap className="w-5 h-5 text-indigo-300" />
          {phase === 1 && <span className="absolute inset-0 rounded-2xl border border-indigo-400/50 animate-ping" />}
        </div>
        <p className="mt-2 text-[10px] font-num uppercase tracking-[0.18em] text-zinc-500">reads it</p>
      </motion.div>

      <Window label="generated proposal">
        {phase >= 2 ? (
          <div className="space-y-2">
            {[
              ["Scope of work", "Design, build, migrate, train"],
              ["Timeline", "6 weeks · 3 milestones"],
              ["Pricing", "₹5,00,000"],
              ["Payment", "40% / 40% / 20%"],
            ].map(([k, v], i) => (
              <motion.div
                key={k}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.14 }}
                className="flex justify-between gap-4 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2"
              >
                <span className="text-[11px] text-zinc-500">{k}</span>
                <span className="text-[11px] font-num text-zinc-200">{v}</span>
              </motion.div>
            ))}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="pt-1 text-[11px] text-emerald-300 inline-flex items-center gap-1.5"
            >
              <Check className="w-3 h-3" /> Client-ready · edit before sending
            </motion.p>
          </div>
        ) : (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-8 rounded-lg bg-zinc-800/40 animate-pulse" />
            ))}
          </div>
        )}
      </Window>
    </div>
  );
};

/** Cursor editing a generated section. */
const EditDemo = () => {
  const ref = useRef(null);
  const seen = useInView(ref, { once: true, margin: "-90px" });
  const [edited, setEdited] = useState(false);
  useEffect(() => {
    if (seen) {
      const t = setTimeout(() => setEdited(true), 1400);
      return () => clearTimeout(t);
    }
  }, [seen]);
  return (
    <div ref={ref} data-testid="edit-demo">
      <Window label="proposal editor">
        <div className="space-y-2">
          <div className="rounded-xl border border-indigo-500/40 bg-indigo-500/[0.07] px-3.5 py-3 relative">
            <p className="text-[11px] text-zinc-500">Deliverables</p>
            <motion.p animate={{ opacity: 1 }} className="mt-1 text-sm text-zinc-100">
              12 pages, CMS, analytics{edited ? ", 3 months of support" : ""}
            </motion.p>
            <motion.span
              initial={{ opacity: 0, x: 20, y: 14 }}
              animate={seen ? { opacity: 1, x: 0, y: 0 } : {}}
              transition={{ duration: 0.7 }}
              className="absolute right-3 bottom-2 text-indigo-300"
            >
              <MousePointer2 className="w-4 h-4" />
            </motion.span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[
              ["Add section", Layers],
              ["Rewrite with AI", Sparkles],
              ["Highlight", Highlighter],
              ["Remove", Ban],
            ].map(([label, Icon]) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 px-2.5 py-1.5 text-[11px] text-zinc-400"
              >
                <Icon className="w-3 h-3" />
                {label}
              </span>
            ))}
          </div>
          <AnimatePresence>
            {edited && (
              <motion.p
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-[11px] text-emerald-300 inline-flex items-center gap-1.5"
              >
                <Check className="w-3 h-3" /> Saved · every section stays editable
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </Window>
    </div>
  );
};

const AUDIENCE = [
  ["Agency owners", Building2],
  ["Freelancers", PenLine],
  ["Business owners", Briefcase],
  ["Enterprise sales teams", TrendingUp],
  ["Founders", Rocket],
  ["Marketers", Megaphone],
  ["IT solution providers", Code2],
];

const STEPS = [
  ["Create a proposal", "Start from blank, a template or a past win.", FileText],
  ["Answer a few questions", "Client, scope, timeline, pricing.", PenLine],
  ["Generate", "Every section drafted for you.", Sparkles],
  ["Edit", "Change anything before it goes out.", Highlighter],
  ["Send", "A branded link, not an email attachment.", Send],
  ["Track", "Views, acceptance and revenue.", BarChart3],
];

const BEFORE = [
  "Client asks for a proposal",
  "Open a blank document",
  "Write everything manually",
  "Format it, again",
  "Calculate pricing by hand",
  "Build the timeline",
  "Send and follow up",
  "Wonder if they even opened it",
];

const AFTER = [
  "Client request comes in",
  "Answer questions or paste the transcript",
  "Proposal generated",
  "Edit what you want",
  "Send a branded link",
  "See views and acceptance",
  "Close",
];

const PLAN_CARDS = [
  { key: "monthly", label: "Monthly", price: INR_PRICES.monthly, per: "/month", proposals: 50, seats: 3, save: null },
  { key: "yearly", label: "Yearly", price: INR_PRICES.yearly, per: "/year", proposals: 700, seats: 10, save: 37, popular: true },
  { key: "quarterly", label: "Quarterly", price: INR_PRICES.quarterly, per: "/quarter", proposals: 165, seats: 5, save: 17 },
];

/** Three glance-able outcomes, so a cold visitor gets it without reading. */
const PLAN_FEATURES = [
  "AI proposal generation (wizard + transcript)",
  "All 15 editor blocks & drag-and-drop",
  "Unlimited brand kits & templates",
  "Client CRM with website auto-fill",
  "Multi-currency + GST engine",
  "Secure share links with password & expiry",
  "Open, time-spent & section tracking",
  "Online acceptance with e-signature",
  "PDF & Word export",
  "Analytics dashboard with funnel & CSV",
];

const LEGAL = [
  ["About us", "/about"],
  ["Contact us", "/contact"],
  ["Privacy policy", "/privacy"],
  ["Terms of service", "/terms"],
  ["Refund policy", "/refund-policy"],
];

const GLANCE = [
  ["Minutes, not days", "Answer a few questions and the whole proposal is drafted.", Timer],
  ["Straight from your meeting", "Paste the transcript. Propojo writes the proposal.", Zap],
  ["You see what happens next", "Opens, time on pricing, acceptance and revenue.", Eye],
];

/* ---------------------------------- page ---------------------------------- */

export default function MetaLanding() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fired = useRef({});

  const campaign = () => {
    const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
    return keys.reduce((acc, k) => (params.get(k) ? { ...acc, [k]: params.get(k) } : acc), {});
  };

  useEffect(() => {
    // stable id so a re-mount (or React's double effect) cannot double-count the visit
    track(
      "ViewContent",
      { content_name: "meta-proposals-lp", content_category: "campaign", ...campaign() },
      { eventId: `lp_view_${Math.floor(Date.now() / 60000)}` },
    );
    const onScroll = () => {
      const h = document.documentElement;
      const pct = Math.round(((h.scrollTop + window.innerHeight) / h.scrollHeight) * 100);
      [25, 50, 75, 100].forEach((mark) => {
        if (pct >= mark && !fired.current[mark]) {
          fired.current[mark] = true;
          track(
            "ScrollDepth",
            { content_name: "meta-proposals-lp", depth: mark, ...campaign() },
            { eventId: `lp_depth_${mark}_${Math.floor(Date.now() / 60000)}` },
          );
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cta = (placement) => {
    const utm = campaign();
    track("CTAClick", { content_name: "create-first-proposal", placement, ...utm });
    track("StartTrial", { currency: "INR", value: 0, predicted_ltv: 9588, content_name: `lp_${placement}`, ...utm });
    const qs = new URLSearchParams(utm).toString();
    navigate(`/signup${qs ? `?${qs}` : ""}`);
  };

  const buy = (planKey) => {
    const utm = campaign();
    track("CTAClick", { content_name: "instant-buy", placement: `plan_${planKey}`, ...utm });
    track("InitiateCheckout", {
      currency: "INR", value: INR_PRICES[planKey], content_ids: [planKey], num_items: 1, ...utm,
    });
    // Login/Plans already forward a stored plan straight into checkout after auth.
    sessionStorage.setItem("pj_plan", planKey);
    const qs = new URLSearchParams(utm).toString();
    navigate(`/signup${qs ? `?${qs}` : ""}`);
  };

  const Cta = ({ placement, label = "Create your first proposal", className = "" }) => (
    <button
      data-testid={`lp-cta-${placement}`}
      onClick={() => cta(placement)}
      className={`group h-12 px-6 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 transition-all hover:shadow-[0_10px_40px_-12px_rgba(99,102,241,0.7)] ${className}`}
    >
      {label}
      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
    </button>
  );

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 24, restDelta: 0.001 });

  return (
    <div className="min-h-screen nv-grid-bg text-zinc-100 pb-24 sm:pb-0">
      <motion.div
        style={{ scaleX: progress }}
        className="fixed top-0 left-0 right-0 z-[60] h-0.5 origin-left bg-gradient-to-r from-indigo-500 via-sky-400 to-emerald-400"
      />
      {/* header: logo only, no navigation away from the funnel */}
      <header className="mx-auto max-w-6xl px-5 py-5 flex items-center justify-between">
        <Logo size={26} withWordmark />
        <div className="flex items-center gap-2">
        <Link
          to="/login"
          data-testid="lp-signin-link"
          className="h-9 px-3 grid place-items-center rounded-lg text-xs text-zinc-400 hover:text-zinc-100 transition-colors"
        >
          Sign in
        </Link>
        <button
          data-testid="lp-cta-header"
          onClick={() => cta("header")}
          className="h-9 px-4 rounded-lg border border-indigo-500/40 bg-indigo-500/10 text-indigo-200 text-xs font-medium hover:bg-indigo-500/20 transition-colors"
        >
          Start free
        </button>
        </div>
      </header>

      {/* 1 · HERO */}
      <section data-testid="lp-hero" className="relative mx-auto max-w-6xl px-5 pt-8 sm:pt-14">
        {/* motion aurora: pure CSS transforms, no images to download */}
        <motion.div
          aria-hidden
          animate={{ x: [0, 40, 0], y: [0, -24, 0], opacity: [0.45, 0.7, 0.45] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          className="pointer-events-none absolute -top-24 -left-10 w-[26rem] h-[26rem] rounded-full bg-indigo-600/20 blur-[90px]"
        />
        <motion.div
          aria-hidden
          animate={{ x: [0, -30, 0], y: [0, 30, 0], opacity: [0.3, 0.55, 0.3] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          className="pointer-events-none absolute top-10 right-0 w-[22rem] h-[22rem] rounded-full bg-sky-500/15 blur-[90px]"
        />
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 items-center">
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-3 py-1 text-[10px] font-num uppercase tracking-[0.18em] text-indigo-300">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-indigo-400 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-indigo-300" />
                </span>
                AI proposal software
              </span>
            </Reveal>
            <Reveal delay={0.06}>
              <h1 className="mt-5 max-w-[19ch] text-[2rem] leading-[1.22] sm:text-[2.6rem] sm:leading-[1.2] lg:text-[3.1rem] lg:leading-[1.16] font-bold tracking-[-0.01em] text-balance">
                From draft to signed shouldn't take days.
                <span className="block mt-2 text-indigo-400">It should take 30 seconds.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-5 max-w-xl text-sm md:text-base text-zinc-400">
                Create professional proposals in minutes with AI. Answer a few simple questions or paste your client
                meeting transcript — Propojo turns it into a complete, client-ready proposal with scope, timeline,
                pricing and payment breakdown.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
                <Cta placement="hero" />
                <p className="text-[11px] text-zinc-500">Create a proposal in minutes. Edit before sending.</p>
              </div>
            </Reveal>
            <Reveal delay={0.24} className="mt-7">
              <StatusPipeline />
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <Window label="propojo · proposal" className="lg:rotate-[0.6deg]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-num uppercase tracking-[0.18em] text-zinc-500">Proposal</p>
                  <p className="mt-1 text-sm font-semibold">Website redesign + SEO</p>
                  <p className="text-[11px] text-zinc-500">Northwind Retail Pvt Ltd</p>
                </div>
                <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[10px] text-emerald-300">
                  Accepted
                </span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {[
                  ["Value", "₹4.8L"],
                  ["Timeline", "6 wks"],
                  ["Milestones", "3"],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-3 py-2.5">
                    <p className="text-[9px] font-num uppercase tracking-[0.14em] text-zinc-500">{k}</p>
                    <p className="mt-0.5 text-sm font-num font-bold">{v}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <AssemblingProposal testid="hero-proposal-assembly" />
              </div>
            </Window>
          </Reveal>
        </div>
      </section>

      {/* understand-in-15-seconds strip */}
      <div className="mx-auto max-w-6xl px-5 mt-14 sm:mt-20 grid sm:grid-cols-3 gap-2.5">
        {GLANCE.map(([title, sub, Icon], i) => (
          <Reveal key={title} delay={i * 0.07}>
            <div className="group h-full rounded-2xl border border-zinc-800 bg-zinc-900/30 p-4 transition-all hover:border-indigo-500/40 hover:-translate-y-0.5">
              <span className="grid place-items-center h-9 w-9 rounded-xl border border-indigo-500/30 bg-indigo-500/10">
                <Icon className="w-4 h-4 text-indigo-300" />
              </span>
              <p className="mt-3 text-sm font-semibold tracking-tight">{title}</p>
              <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">{sub}</p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* 2 · PRODUCT DASHBOARD */}
      <Section
        testid="lp-dashboard"
        eyebrow="Your pipeline, live"
        title="Every proposal, its status and its value — in one place."
        sub="This is the Propojo dashboard: what went out, what was opened, what was accepted."
      >
        <Reveal>
          <Window label="propojo · analytics">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              {[
                ["Proposals sent", <Counter to={38} />, Send],
                ["Viewed by clients", <Counter to={31} />, Eye],
                ["Accepted", <Counter to={12} />, BadgeCheck],
                ["Value accepted", <Counter to={2140000} prefix="₹" />, IndianRupee],
              ].map(([label, value, Icon], i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3.5"
                >
                  <Icon className="w-3.5 h-3.5 text-indigo-300" />
                  <p className="mt-2 text-lg sm:text-xl font-bold tracking-tight">{value}</p>
                  <p className="text-[10px] text-zinc-500">{label}</p>
                </motion.div>
              ))}
            </div>
            <div className="mt-3 flex items-end gap-1.5 h-24">
              {[38, 52, 44, 66, 58, 78, 71, 92, 84, 100, 88, 96].map((h, i) => (
                <motion.span
                  key={i}
                  initial={{ height: 4 }}
                  whileInView={{ height: `${h}%` }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.5 }}
                  className="flex-1 rounded-t bg-gradient-to-t from-indigo-500/25 to-indigo-400/80"
                />
              ))}
            </div>
            <p className="mt-2 text-[10px] text-zinc-600">Illustrative product interface.</p>
          </Window>
        </Reveal>
      </Section>

      {/* 3 · PAIN */}
      <Section
        testid="lp-pain"
        eyebrow="The way it works today"
        title="The proposal is the bottleneck — not the work."
        sub="Every request restarts the same manual routine, and the deal waits."
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
          {BEFORE.slice(0, 8).map((t, i) => (
            <Reveal key={t} delay={i * 0.04}>
              <div className="h-full rounded-xl border border-zinc-800 bg-zinc-900/30 px-3.5 py-3">
                <p className="text-[10px] font-num text-zinc-600">{String(i + 1).padStart(2, "0")}</p>
                <p className="mt-1 text-xs text-zinc-400">{t}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 4+5 · SOLUTION + 6 QUESTIONS */}
      <Section
        testid="lp-wizard"
        eyebrow="The Propojo way"
        title="Answer a few questions. Get a complete proposal."
        sub="Client, project, requirements, scope, timeline, pricing and payment structure — that's all Propojo needs."
      >
        <div className="grid lg:grid-cols-2 gap-4 items-start">
          <Reveal>
            <Window label="new proposal · questions">
              <WizardDemo />
            </Window>
          </Reveal>
          {/* 6 · GENERATED PROPOSAL */}
          <Reveal delay={0.1}>
            <Window label="generated proposal">
              <p className="text-sm font-semibold">Your proposal is ready.</p>
              <p className="mt-1 text-[11px] text-zinc-500">Every section drafted, priced and formatted.</p>
              <div className="mt-4">
                <AssemblingProposal testid="generated-proposal-sections" />
              </div>
              <div className="mt-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
                <div className="flex justify-between text-[11px] text-zinc-500">
                  <span>Payment breakdown</span>
                  <span className="font-num text-zinc-300">₹4,80,000</span>
                </div>
                <div className="mt-2 flex gap-1.5">
                  {[40, 40, 20].map((w, i) => (
                    <motion.div
                      key={i}
                      initial={{ width: 0 }}
                      whileInView={{ width: `${w}%` }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.9 + i * 0.15, duration: 0.5 }}
                      className={`h-2 rounded-full ${["bg-indigo-400", "bg-sky-400", "bg-emerald-400"][i]}`}
                    />
                  ))}
                </div>
                <p className="mt-1.5 text-[10px] text-zinc-600">Upfront 40% · Design sign-off 40% · Launch 20%</p>
              </div>
            </Window>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="mt-8">
          <Cta placement="wizard" />
        </Reveal>
      </Section>

      {/* 7 · TRANSCRIPT */}
      <Section
        testid="lp-transcript"
        eyebrow="Meeting to proposal"
        title="Your meeting already has everything you need."
        sub="Don't spend another hour turning meeting notes into a proposal. Paste the transcript — Propojo reads the conversation and writes the proposal."
      >
        <TranscriptDemo />
      </Section>

      {/* 8 · EDIT */}
      <Section
        testid="lp-edit"
        eyebrow="Full control"
        title="Need to change something? Just change it."
        sub="Add, edit, remove or rewrite any section. Nothing goes to your client until you say so."
      >
        <div className="grid lg:grid-cols-2 gap-4 items-center">
          <EditDemo />
          <Reveal delay={0.08}>
            <ul className="space-y-2.5">
              {[
                "Rewrite any section with AI, or type it yourself",
                "Add scope, deliverables or milestones in one click",
                "Adjust pricing and payment split — totals recalculate",
                "Apply your brand kit: logo, colours, fonts",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-zinc-400">
                  <Check className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </Section>

      {/* 9 · TRACKING */}
      <Section
        testid="lp-tracking"
        eyebrow="After you hit send"
        title="Stop sending proposals into a black hole."
        sub="See when a proposal is opened, how long each section held attention, and when it's accepted."
      >
        <Reveal>
          <Window label="propojo · proposal tracking">
            <div className="space-y-2">
              {[
                ["Website redesign + SEO", "Accepted", "₹4,80,000", "text-emerald-300 border-emerald-500/40"],
                ["Performance marketing retainer", "Viewed 4×", "₹1,20,000", "text-amber-300 border-amber-500/40"],
                ["ERP implementation", "Sent", "₹9,50,000", "text-sky-300 border-sky-500/40"],
              ].map(([name, status, value, tone], i) => (
                <motion.div
                  key={name}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 px-3.5 py-3"
                >
                  <FileText className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                  <span className="flex-1 text-xs text-zinc-300 truncate">{name}</span>
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] ${tone}`}>{status}</span>
                  <span className="w-24 text-right text-[11px] font-num text-zinc-300">{value}</span>
                </motion.div>
              ))}
            </div>
            <div className="mt-3 grid sm:grid-cols-3 gap-2.5">
              {[
                ["First opened", "4 min after sending", Clock],
                ["Time on pricing", "1 min 12 sec", Timer],
                ["Accepted with e-sign", "Same day", BadgeCheck],
              ].map(([k, v, Icon]) => (
                <div key={k} className="rounded-xl border border-zinc-800 bg-zinc-900/40 px-3.5 py-3">
                  <Icon className="w-3.5 h-3.5 text-indigo-300" />
                  <p className="mt-1.5 text-xs text-zinc-200">{v}</p>
                  <p className="text-[10px] text-zinc-500">{k}</p>
                </div>
              ))}
            </div>
            <p className="mt-2 text-[10px] text-zinc-600">Illustrative product interface.</p>
          </Window>
        </Reveal>
      </Section>

      {/* 10 · BEFORE vs AFTER */}
      <Section testid="lp-before-after" eyebrow="Before vs after" title="Same request. Very different day.">
        <div className="grid lg:grid-cols-2 gap-4">
          <Reveal>
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/20 p-5 h-full">
              <p className="text-[10px] font-num uppercase tracking-[0.18em] text-zinc-500">Before Propojo</p>
              <ol className="mt-4 space-y-2">
                {BEFORE.map((t) => (
                  <li key={t} className="flex items-start gap-2 text-xs text-zinc-500">
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-zinc-600 shrink-0" />
                    {t}
                  </li>
                ))}
              </ol>
              <p className="mt-4 text-[11px] text-zinc-600">Hours of work, then silence.</p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="relative rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/12 to-transparent p-5 h-full overflow-hidden">
              <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full bg-indigo-500/15 blur-3xl" />
              <p className="relative text-[10px] font-num uppercase tracking-[0.18em] text-indigo-300">With Propojo</p>
              <ol className="relative mt-4 space-y-2">
                {AFTER.map((t, i) => (
                  <motion.li
                    key={t}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.07 }}
                    className="flex items-start gap-2 text-sm text-zinc-200"
                  >
                    <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-400 shrink-0" />
                    {t}
                  </motion.li>
                ))}
              </ol>
              <p className="relative mt-4 text-[11px] text-indigo-200">Minutes of work, and you know what happens next.</p>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* 11 · WHO IT'S FOR */}
      <Section testid="lp-audience" eyebrow="Who it's for" title="Built for people who sell services.">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
          {AUDIENCE.map(([label, Icon], i) => (
            <Reveal key={label} delay={i * 0.04}>
              <div className="h-full rounded-xl border border-zinc-800 bg-zinc-900/30 px-3.5 py-4 hover:border-indigo-500/40 transition-colors">
                <Icon className="w-4 h-4 text-indigo-300" />
                <p className="mt-2.5 text-xs text-zinc-300">{label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 12 · HOW IT WORKS */}
      <Section testid="lp-how" eyebrow="How it works" title="Six steps from request to closed.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {STEPS.map(([title, sub, Icon], i) => (
            <Reveal key={title} delay={i * 0.06}>
              <div className="h-full rounded-2xl border border-zinc-800 bg-zinc-900/30 p-4 hover:border-indigo-500/40 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-num text-indigo-400">{String(i + 1).padStart(2, "0")}</span>
                  <Icon className="w-4 h-4 text-zinc-500" />
                </div>
                <p className="mt-3 text-sm font-semibold">{title}</p>
                <p className="mt-1 text-[11px] text-zinc-500">{sub}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* PRICING · instant buy */}
      <Section
        testid="lp-pricing"
        eyebrow="Simple, professional billing"
        title="Start free. Upgrade the moment it pays for itself."
        sub="Every plan includes every feature — AI drafting, transcript-to-proposal, branding, e-signature, tracking and exports. Longer terms simply cost less per month."
      >
        <div className="grid lg:grid-cols-3 gap-3">
          {PLAN_CARDS.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.07}>
              <div
                data-testid={`lp-plan-${p.key}`}
                className={`relative h-full overflow-hidden rounded-2xl border p-5 transition-all hover:-translate-y-1 ${
                  p.popular
                    ? "border-indigo-500/50 bg-gradient-to-b from-indigo-500/14 to-transparent shadow-[0_20px_60px_-30px_rgba(99,102,241,0.8)]"
                    : "border-zinc-800 bg-zinc-900/30 hover:border-zinc-700"
                }`}
              >
                {p.popular && (
                  <span className="absolute top-4 right-4 rounded-full bg-indigo-500 px-2.5 py-0.5 text-[9px] font-num uppercase tracking-[0.16em] text-white">
                    best value
                  </span>
                )}
                <p className="text-[10px] font-num uppercase tracking-[0.18em] text-zinc-500">{p.label}</p>
                <p className="mt-3 flex items-baseline gap-1.5">
                  <span className="text-3xl sm:text-4xl font-extrabold font-num tracking-tight">
                    ₹{p.price.toLocaleString("en-IN")}
                  </span>
                  <span className="text-[11px] text-zinc-500">{p.per}</span>
                </p>
                {p.save ? (
                  <motion.span
                    initial={{ opacity: 0, y: 6 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-2 inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-300"
                  >
                    <TrendingUp className="w-3 h-3" /> save {p.save}%
                  </motion.span>
                ) : (
                  <span className="mt-2 inline-block text-[10px] text-zinc-600">pay as you go</span>
                )}
                <ul className="mt-4 space-y-1.5">
                  {[`${p.proposals} proposals`, `${p.seats} team seats`, "Cancel anytime"].map((t) => (
                    <li key={t} className="flex items-center gap-2 text-xs font-medium text-zinc-200">
                      <Check className="w-3.5 h-3.5 text-indigo-300 shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
                <p className="mt-4 pt-3 border-t border-zinc-800 text-[10px] font-num uppercase tracking-[0.16em] text-zinc-500">
                  Everything included
                </p>
                <ul className="mt-2.5 space-y-1.5">
                  {PLAN_FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[11px] leading-relaxed text-zinc-400">
                      <Check className="w-3 h-3 mt-0.5 text-emerald-400 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`lp-buy-${p.key}`}
                  onClick={() => buy(p.key)}
                  className={`mt-5 w-full h-11 rounded-xl text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ${
                    p.popular
                      ? "bg-indigo-500 hover:bg-indigo-600 text-white"
                      : "border border-zinc-800 hover:border-indigo-500/50 text-zinc-200"
                  }`}
                >
                  Get {p.label.toLowerCase()} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.12}>
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-zinc-800 bg-zinc-900/30 px-4 py-3">
            {[
              "Free to start — no card needed",
              "UPI, cards & netbanking",
              "256-bit SSL · PCI-DSS gateway",
              "GST invoice on every payment",
            ].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400">
                <BadgeCheck className="w-3.5 h-3.5 text-emerald-400" />
                {t}
              </span>
            ))}
          </div>
        </Reveal>
      </Section>

      {/* 13+14 · FINAL VALUE + CTA */}
      <section data-testid="lp-final-cta" className="mx-auto max-w-6xl px-5 pb-20">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/15 via-transparent to-transparent p-7 sm:p-12 text-center">
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[28rem] h-[28rem] rounded-full bg-indigo-500/15 blur-3xl" />
            <div className="relative">
              <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">
                Take the request. Send the proposal. Know what happens next.
              </h2>
              <p className="mt-3 mx-auto max-w-xl text-sm md:text-base text-zinc-400">
                Start free — no card needed. Create your first proposal today and keep everything you make.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Cta placement="final" />
                <span className="text-[11px] text-zinc-500">Professional billing when you're ready to scale.</span>
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
                {["Free to start", "Your branding on every proposal", "PDF & Word export", "Secure share links"].map(
                  (t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400">
                      <Check className="w-3 h-3 text-emerald-400" />
                      {t}
                    </span>
                  ),
                )}
              </div>
            </div>
          </div>
        </Reveal>
        <footer className="mt-10 border-t border-zinc-800 pt-6">
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {LEGAL.map(([label, to]) => (
              <Link
                key={to}
                to={to}
                data-testid={`lp-footer-${to.replace(/\//g, "")}`}
                className="text-[11px] text-zinc-500 hover:text-indigo-300 transition-colors"
              >
                {label}
              </Link>
            ))}
          </div>
          <p className="mt-4 text-center text-[10px] text-zinc-600">
            © {new Date().getFullYear()} Propojo · Interfaces shown are product illustrations.
          </p>
        </footer>
      </section>

      {/* sticky mobile CTA */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-zinc-800 bg-[#0a0a0f]/95 backdrop-blur px-4 py-3">
        <Cta placement="sticky-mobile" className="w-full" />
      </div>
    </div>
  );
}
